import { Question, Subject } from '../types';
import { DeepSeekService } from './deepseekApi';
import { API_CONFIG, DEV_CONFIG } from '../config/api';

export interface HintLevel {
  level: 1 | 2 | 3;
  title: string;
  content: string;
  encouragement?: string;
}

export interface SmartHintResult {
  shouldShowHint: boolean;
  hint?: HintLevel;
  encouragement?: string;
}

export class SmartHintService {
  private deepseekService: DeepSeekService | null = null;

  constructor() {
    if (API_CONFIG.DEEPSEEK.API_KEY) {
      this.deepseekService = new DeepSeekService(API_CONFIG.DEEPSEEK.API_KEY);
    }
  }

  async analyzeNeedForHint(
    question: Question,
    timeSpent: number,
    hasSelectedAnswer: boolean,
    previousHintLevel: number = 0
  ): Promise<SmartHintResult> {
    const timeLimit = question.time_limit || 60;
    const timeRatio = timeSpent / (timeLimit * 1000);
    
    // 调试信息
    console.log('Smart Hint Analysis:', {
      questionId: question.id,
      subject: question.subject,
      timeSpent: Math.round(timeSpent / 1000),
      timeLimit,
      timeRatio: timeRatio.toFixed(2),
      hasSelectedAnswer,
      previousHintLevel
    });
    
    // 优化的判断逻辑 - 增加提示间隔时间
    let shouldShowHint = false;
    let hintLevel: 1 | 2 | 3 = 1;

    // 语文题目提示时机
    if (question.subject === 'chinese') {
      if (timeRatio > 0.4 && !hasSelectedAnswer && previousHintLevel === 0) {
        shouldShowHint = true;
        hintLevel = 1;
      } else if (timeRatio > 0.8 && !hasSelectedAnswer && previousHintLevel <= 1) {
        shouldShowHint = true;
        hintLevel = 2;
      } else if (timeRatio > 1.2 && !hasSelectedAnswer && previousHintLevel <= 2) {
        shouldShowHint = true;
        hintLevel = 3;
      }
    } else {
      // 数学题目提示时机 - 延长间隔
      if (timeRatio > 0.5 && !hasSelectedAnswer && previousHintLevel === 0) {
        shouldShowHint = true;
        hintLevel = 1;
      } else if (timeRatio > 1.0 && !hasSelectedAnswer && previousHintLevel <= 1) {
        shouldShowHint = true;
        hintLevel = 2;
      } else if (timeRatio > 1.3 && !hasSelectedAnswer && previousHintLevel <= 2) {
        shouldShowHint = true;
        hintLevel = 3;
      }
    }

    // 确保每个等级之间有足够的时间间隔
    const minIntervalSeconds = 15; // 最少15秒间隔
    const timeInSeconds = timeSpent / 1000;
    
    if (shouldShowHint && previousHintLevel > 0) {
      // 如果已经显示过提示，需要检查间隔时间
      const expectedMinTime = previousHintLevel * minIntervalSeconds;
      if (timeInSeconds < expectedMinTime) {
        shouldShowHint = false;
        console.log('Hint suppressed due to insufficient interval:', {
          timeInSeconds,
          expectedMinTime,
          previousHintLevel
        });
      }
    }

    console.log('Hint Decision:', { shouldShowHint, hintLevel });

    if (!shouldShowHint) {
      return { shouldShowHint: false };
    }

    // 生成智能提示
    const hint = await this.generateSmartHint(question, hintLevel);
    
    return {
      shouldShowHint: true,
      hint
    };
  }

  private async generateSmartHint(question: Question, level: 1 | 2 | 3): Promise<HintLevel> {
    if (DEV_CONFIG.USE_MOCK_AI || !this.deepseekService) {
      return this.getMockHint(question, level);
    }

    try {
      const hintPrompt = this.buildHintPrompt(question, level);
      
      const response = await fetch(API_CONFIG.DEEPSEEK.BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_CONFIG.DEEPSEEK.API_KEY}`,
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: `你是一个智慧的AI学习助手。你的目标是引导学生思考，而不是直接给出答案。
              
要求：
1. 提示要循序渐进，不能直接透露答案
2. 要根据提示等级给出合适的引导
3. 语言要亲切、鼓励，像一个耐心的老师
4. 返回JSON格式
5. 用中文回答`
            },
            {
              role: 'user',
              content: hintPrompt
            }
          ],
          temperature: 0.7,
          max_tokens: 300
        })
      });

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (content) {
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const result = JSON.parse(jsonMatch[0]);
          return {
            level,
            title: result.title || '💡 小提示',
            content: result.content || '仔细观察题目，你会发现规律的！',
            encouragement: result.encouragement || '加油，你可以的！'
          };
        }
      }
    } catch (error) {
      console.error('生成智能提示失败:', error);
    }

    return this.getMockHint(question, level);
  }

  private buildHintPrompt(question: Question, level: 1 | 2 | 3): string {
    const levelDescriptions = {
      1: '轻微引导，让学生注意关键信息',
      2: '中等提示，给出解题思路方向', 
      3: '较强提示，但仍不直接给答案'
    };

    return `请为以下题目生成第${level}级提示（${levelDescriptions[level]}）：

题目信息：
- 学科：${question.subject === 'math' ? '数学' : '语文'}
- 认知维度：${this.getDimensionName(question.skill_dimension)}
- 难度：${question.difficulty}/5
- 题目：${question.content.title}
- 选项：${question.content.options?.join(', ') || '无'}

提示等级说明：
- 1级：轻微引导，提醒学生注意题目中的关键词或数字
- 2级：给出解题的大致方向，但不具体说明步骤
- 3级：给出更具体的思路，但仍然让学生自己完成最后一步

请返回JSON格式：
{
  "title": "提示标题",
  "content": "具体的提示内容",
  "encouragement": "鼓励的话语"
}`;
  }

  private getDimensionName(dimension: string): string {
    const names: { [key: string]: string } = {
      processing_speed: '信息处理速度',
      working_memory: '工作记忆',
      abstract_thinking: '抽象思维',
      pattern_recognition: '模式识别',
      transfer_application: '迁移应用',
      metacognitive_monitoring: '元认知监控'
    };
    return names[dimension] || dimension;
  }

  private getMockHint(question: Question, level: 1 | 2 | 3): HintLevel {
    // 基于题目类型和等级返回模拟提示
    const mockHints = this.generateMockHintsByQuestion(question);
    
    return {
      level,
      title: mockHints[level - 1].title,
      content: mockHints[level - 1].content,
      encouragement: mockHints[level - 1].encouragement
    };
  }

  private generateMockHintsByQuestion(question: Question): HintLevel[] {
    if (question.subject === 'math') {
      if (question.skill_dimension === 'processing_speed') {
        return [
          {
            level: 1,
            title: '💡 仔细观察',
            content: '注意看题目中的数字，有什么运算关系吗？',
            encouragement: '基础计算对你来说应该不难！'
          },
          {
            level: 2,
            title: '🔍 解题思路',
            content: '试试按照运算顺序来计算，先算哪一步呢？',
            encouragement: '一步一步来，你一定能算对！'
          },
          {
            level: 3,
            title: '📝 最后一步',
            content: '把中间结果写下来，然后进行最后的运算。',
            encouragement: '就差最后一步了，相信自己！'
          }
        ];
      } else if (question.skill_dimension === 'pattern_recognition') {
        return [
          {
            level: 1,
            title: '🔍 寻找规律',
            content: '仔细观察这些数字之间的关系，它们是如何变化的？',
            encouragement: '找规律是你的强项，仔细看看！'
          },
          {
            level: 2,
            title: '➕ 关系分析',
            content: '试试看相邻的数字之间是乘法、加法还是其他关系？',
            encouragement: '很好，你已经在正确的道路上了！'
          },
          {
            level: 3,
            title: '🎯 应用规律',
            content: '找到规律后，把这个规律应用到最后一个位置。',
            encouragement: '规律已经很明显了，最后一步加油！'
          }
        ];
      }
    } else if (question.subject === 'chinese') {
      if (question.skill_dimension === 'processing_speed') {
        return [
          {
            level: 1,
            title: '📖 仔细阅读',
            content: '再读一遍题目，注意其中的关键词。',
            encouragement: '语文需要细心，你一定可以的！'
          },
          {
            level: 2,
            title: '💭 理解含义',
            content: '想想这个词语或句子在什么情况下使用？',
            encouragement: '你的语言感觉很好，相信直觉！'
          },
          {
            level: 3,
            title: '✅ 排除选项',
            content: '可以先排除明显不对的选项，再从剩下的中选择。',
            encouragement: '排除法是很好的策略，试试看！'
          }
        ];
      } else if (question.skill_dimension === 'pattern_recognition') {
        return [
          {
            level: 1,
            title: '🔍 寻找联系',
            content: '这是一首古诗，想想你学过的相关诗句。',
            encouragement: '你的文学基础很好，回忆一下！'
          },
          {
            level: 2,
            title: '📚 诗词知识',
            content: '想想这首诗的作者和朝代，这能帮你找到答案。',
            encouragement: '古诗词的学习需要积累，你做得很好！'
          },
          {
            level: 3,
            title: '🎵 诗句韵律',
            content: '可以试着在心里念一遍，感受诗句的韵律和意境。',
            encouragement: '用心感受，答案就在其中！'
          }
        ];
      } else if (question.skill_dimension === 'abstract_thinking') {
        return [
          {
            level: 1,
            title: '🤔 深度思考',
            content: '这道题需要理解背后的含义，不要只看表面意思。',
            encouragement: '你的理解能力很强，深入思考一下！'
          },
          {
            level: 2,
            title: '🔗 联想分析',
            content: '想想这个成语或概念在现实生活中的应用场景。',
            encouragement: '联系实际生活，你会有新的理解！'
          },
          {
            level: 3,
            title: '💡 核心理解',
            content: '抓住题目想要考查的核心概念和深层含义。',
            encouragement: '你已经很接近答案了，相信自己的判断！'
          }
        ];
      } else if (question.skill_dimension === 'working_memory') {
        return [
          {
            level: 1,
            title: '📝 理清思路',
            content: '这道题涉及多个信息，先把关键信息标记出来。',
            encouragement: '整理信息是你的强项，慢慢来！'
          },
          {
            level: 2,
            title: '🔄 重新组织',
            content: '按照逻辑顺序重新排列这些词汇或信息。',
            encouragement: '你的逻辑思维很清晰，继续！'
          },
          {
            level: 3,
            title: '✅ 验证答案',
            content: '把你选择的答案放回原句中，看看是否通顺自然。',
            encouragement: '验证是好习惯，你做得很棒！'
          }
        ];
      } else if (question.skill_dimension === 'transfer_application') {
        return [
          {
            level: 1,
            title: '🌍 拓展思维',
            content: '这道题考查的是知识的应用，想想相关的知识点。',
            encouragement: '你的知识面很广，好好运用！'
          },
          {
            level: 2,
            title: '🔗 知识迁移',
            content: '把你学过的文化知识应用到这个具体情境中。',
            encouragement: '知识迁移能力很重要，你正在提升！'
          },
          {
            level: 3,
            title: '🎯 实际应用',
            content: '想想如果你真的遇到这种情况，应该怎么处理或表达。',
            encouragement: '理论联系实际，这是最好的学习方法！'
          }
        ];
      }
    }

    // 默认提示
    return [
      {
        level: 1,
        title: '💡 小提示',
        content: '仔细观察题目，找到关键信息。',
        encouragement: '你可以的，静下心来思考！'
      },
      {
        level: 2,
        title: '🤔 思考方向',
        content: '试试从不同角度分析这个问题。',
        encouragement: '很好，继续这样思考下去！'
      },
      {
        level: 3,
        title: '🎯 解题策略',
        content: '运用你学过的方法和技巧来解决。',
        encouragement: '就快成功了，最后再努力一下！'
      }
    ];
  }

  // 生成鼓励话语
  generateEncouragement(timeSpent: number, difficulty: number): string {
    const encouragements = [
      '你思考得很认真，这是好习惯！',
      '慢慢来，深度思考比速度更重要。',
      '每一次思考都是进步的过程。',
      '遇到难题很正常，坚持就是胜利！',
      '你的学习态度很棒，继续保持！'
    ];
    
    return encouragements[Math.floor(Math.random() * encouragements.length)];
  }
}